import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Target, Save, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_META = {
  to_approach:    { label: "To approach",    cls: "bg-muted text-muted-foreground" },
  contacted:      { label: "Contacted",      cls: "bg-primary/15 text-primary" },
  meeting_booked: { label: "Meeting booked", cls: "bg-warning/15 text-warning" },
  pilot_agreed:   { label: "Pilot agreed",   cls: "bg-success/15 text-success" },
  declined:       { label: "Declined",       cls: "bg-destructive/15 text-destructive" },
};

function ProspectRow({ p, onChanged }) {
  const [draft, setDraft] = useState(p);
  const [dirty, setDirty] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); setDirty(true); };

  const emailTo = (draft.contact_info || "").trim();
  const canSend = emailTo.includes("@");

  const sendOpportunity = async () => {
    if (!window.confirm(`Send the founding-partner opportunity email (with the Toyota pitch PDF attached) to ${emailTo}?`)) return;
    setSending(true);
    try {
      const r = await api.post(`/prospects/${p.id}/send-opportunity`, {
        to_email: emailTo,
        contact_name: draft.contact_name || "",
      });
      onChanged(r.data.prospect);
      toast.success(`Opportunity email sent to ${emailTo}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send"); }
    finally { setSending(false); }
  };

  const save = async (patch) => {
    try {
      const r = await api.patch(`/prospects/${p.id}`, patch);
      onChanged(r.data);
      setDirty(false);
      toast.success(`${p.group_name} updated`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${p.group_name} from the pipeline?`)) return;
    try {
      await api.delete(`/prospects/${p.id}`);
      onChanged(null, p.id);
      toast.success(`${p.group_name} removed`);
    } catch (e) { toast.error("Couldn't remove"); }
  };

  return (
    <div className="card-default p-4 space-y-3" data-testid={`prospect-row-${p.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-lg truncate">{p.group_name}</div>
          <div className="text-xs text-muted-foreground">
            {p.region}{p.toyota_sites ? ` · ${p.toyota_sites} Toyota sites` : ""}
          </div>
        </div>
        <select
          value={draft.status}
          onChange={(e) => { set("status", e.target.value); save({ status: e.target.value }); }}
          className={`text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1.5 border border-border bg-card cursor-pointer ${STATUS_META[draft.status]?.cls || ""}`}
          data-testid={`prospect-status-${p.id}`}
        >
          {Object.entries(STATUS_META).map(([k, m]) => (
            <option key={k} value={k}>{m.label}</option>
          ))}
        </select>
        <button onClick={remove} className="text-muted-foreground hover:text-destructive p-1.5" title="Remove" data-testid={`prospect-delete-${p.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {p.ownership && <p className="text-xs text-muted-foreground">{p.ownership}</p>}
      <div className="grid sm:grid-cols-3 gap-2">
        <input
          value={draft.contact_name || ""}
          onChange={(e) => set("contact_name", e.target.value)}
          placeholder="Contact name"
          className="bg-muted/20 border border-border rounded-md px-3 py-2 text-sm"
          data-testid={`prospect-contact-name-${p.id}`}
        />
        <input
          value={draft.contact_info || ""}
          onChange={(e) => set("contact_info", e.target.value)}
          placeholder="Phone / email"
          className="bg-muted/20 border border-border rounded-md px-3 py-2 text-sm"
          data-testid={`prospect-contact-info-${p.id}`}
        />
        <input
          value={draft.next_action || ""}
          onChange={(e) => set("next_action", e.target.value)}
          placeholder="Next action (e.g. call Monday)"
          className="bg-muted/20 border border-border rounded-md px-3 py-2 text-sm"
          data-testid={`prospect-next-action-${p.id}`}
        />
      </div>
      <textarea
        value={draft.notes || ""}
        onChange={(e) => set("notes", e.target.value)}
        placeholder="Notes"
        rows={2}
        className="w-full bg-muted/20 border border-border rounded-md px-3 py-2 text-sm"
        data-testid={`prospect-notes-${p.id}`}
      />
      {(dirty || canSend) && (
        <div className="flex flex-wrap gap-2">
          {dirty && (
            <button
              onClick={() => save({ contact_name: draft.contact_name, contact_info: draft.contact_info, next_action: draft.next_action, notes: draft.notes })}
              className="btn-primary"
              data-testid={`prospect-save-${p.id}`}
            >
              <Save className="w-4 h-4" /> Save changes
            </button>
          )}
          {canSend && (
            <button
              onClick={sendOpportunity}
              disabled={sending}
              className="btn-outline"
              title="Emails the founding-partner opportunity + Toyota pitch PDF, and marks this prospect as Contacted"
              data-testid={`prospect-send-opportunity-${p.id}`}
            >
              <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send opportunity email"}
            </button>
          )}
        </div>
      )}
      {(p.outreach || []).length > 0 && (
        <div className="text-[11px] text-muted-foreground" data-testid={`prospect-outreach-log-${p.id}`}>
          ✉️ Sent {p.outreach.length}× — last to {p.outreach[p.outreach.length - 1].to} on {new Date(p.outreach[p.outreach.length - 1].sent_at).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

function AddProspect({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ group_name: "", region: "", toyota_sites: "", ownership: "" });
  const add = async () => {
    if (!form.group_name.trim()) { toast.error("Group name required"); return; }
    try {
      const r = await api.post("/prospects", {
        ...form,
        toyota_sites: form.toyota_sites ? Number(form.toyota_sites) : null,
      });
      onAdded(r.data);
      setForm({ group_name: "", region: "", toyota_sites: "", ownership: "" });
      setOpen(false);
      toast.success("Prospect added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add"); }
  };
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-primary" data-testid="add-prospect-btn">
        <Plus className="w-4 h-4" /> Add prospect
      </button>
    );
  }
  return (
    <div className="card-default p-4 space-y-2 w-full" data-testid="add-prospect-form">
      <div className="grid sm:grid-cols-4 gap-2">
        <input value={form.group_name} onChange={(e) => setForm({ ...form, group_name: e.target.value })}
               placeholder="Group name *" className="bg-muted/20 border border-border rounded-md px-3 py-2 text-sm" data-testid="new-prospect-name" />
        <input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}
               placeholder="Region" className="bg-muted/20 border border-border rounded-md px-3 py-2 text-sm" data-testid="new-prospect-region" />
        <input value={form.toyota_sites} onChange={(e) => setForm({ ...form, toyota_sites: e.target.value.replace(/\D/g, "") })}
               placeholder="Toyota sites" className="bg-muted/20 border border-border rounded-md px-3 py-2 text-sm" data-testid="new-prospect-sites" />
        <input value={form.ownership} onChange={(e) => setForm({ ...form, ownership: e.target.value })}
               placeholder="Ownership notes" className="bg-muted/20 border border-border rounded-md px-3 py-2 text-sm" data-testid="new-prospect-ownership" />
      </div>
      <div className="flex gap-2">
        <button onClick={add} className="btn-primary" data-testid="new-prospect-submit">Add</button>
        <button onClick={() => setOpen(false)} className="btn-outline" data-testid="new-prospect-cancel">Cancel</button>
      </div>
    </div>
  );
}

export default function ProspectsPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/prospects")
      .then((r) => setRows(r.data.prospects || []))
      .catch(() => toast.error("Couldn't load prospects"))
      .finally(() => setLoading(false));
  }, []);

  const onChanged = (updated, deletedId) => {
    if (deletedId) { setRows((rs) => rs.filter((r) => r.id !== deletedId)); return; }
    setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
  };

  if (user && !user.platform_owner) {
    return (
      <div className="min-h-screen bg-background grid place-items-center px-6" data-testid="prospects-gate">
        <div className="text-center space-y-3">
          <h1 className="font-display font-bold text-2xl">Founder only</h1>
          <button onClick={() => nav("/dashboard")} className="btn-primary">Back to dashboard</button>
        </div>
      </div>
    );
  }

  const counts = rows.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});

  return (
    <div className="min-h-screen bg-background" data-testid="prospects-page">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/dashboard")} className="btn-outline" data-testid="prospects-back-btn">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <div className="flex-1">
            <div className="label-overline text-primary flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5" /> Founder pipeline
            </div>
            <h1 className="font-display font-bold text-2xl">Dealer group prospects</h1>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-xs" data-testid="prospects-status-summary">
          {Object.entries(STATUS_META).map(([k, m]) => (
            <span key={k} className={`px-3 py-1.5 rounded-full font-bold uppercase tracking-wider ${m.cls}`}>
              {m.label}: {counts[k] || 0}
            </span>
          ))}
        </div>

        <AddProspect onAdded={(p) => setRows((rs) => [p, ...rs])} />

        {loading ? (
          <div className="text-sm text-muted-foreground py-8">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-8" data-testid="prospects-empty">No prospects yet.</div>
        ) : (
          <div className="space-y-3" data-testid="prospects-list">
            {rows.map((p) => <ProspectRow key={p.id} p={p} onChanged={onChanged} />)}
          </div>
        )}
      </div>
    </div>
  );
}
